import type { EntraGroup, EntraUser, LinkItem, ReviewItemAws, ReviewItemCoreMetadata, ReviewItemGitHub } from '../api';

type Review = ReviewItemAws | ReviewItemGitHub | ReviewItemCoreMetadata;

// AWS and GitHub list members directly on reviewedItem; core-metadata nests them one level down,
// under its accessManagerGroup. Resolve both shapes here rather than teaching every consumer the
// difference.
type ReviewedItemShape = { member?: EntraUser[]; accessManagerGroup?: EntraGroup; links?: LinkItem[] };

function reviewedItem(review: Review): ReviewedItemShape | undefined {
  return review.reviewedItem;
}

export function resolveMembers(review: Review): EntraUser[] {
  const item = reviewedItem(review);
  return item?.accessManagerGroup?.member ?? item?.member ?? [];
}

// links replaced the old single editLink; accessManagerGroup.editLink is a separate, older field
// that can still be set, so keep it as a last-resort single link rather than dropping management
// access for a review that hasn't been migrated to links yet.
export function resolveLinks(review: Review): LinkItem[] {
  const item = reviewedItem(review);
  if (item?.links?.length) return item.links;
  const editLink = item?.accessManagerGroup?.editLink;
  return editLink ? [{ name: 'Manage', link: editLink }] : [];
}

/**
 * "Access Managers" when the members come from an accessManagerGroup (core-metadata today),
 * plain "Members" for a flat member list (AWS/GitHub). Driven by shape, not reviewType, so a
 * future type gets the right label automatically rather than needing a per-type entry.
 */
export function resolveMembersLabel(review: Review): string {
  return reviewedItem(review)?.accessManagerGroup ? 'Access Managers' : 'Members';
}

// Fields already given a dedicated widget elsewhere (name/reference back the page title and the
// list view, member/accessManagerGroup back the member list, links back the action buttons).
const KNOWN_KEYS = new Set(['name', 'reference', 'member', 'links', 'accessManagerGroup']);

/**
 * Every other reviewedItem property, whatever the current review type happens to carry, so a
 * schema addition (core-metadata's budget/contacts/stage fields, or a future type's own) shows up
 * without a frontend change. Keys are shown as-is, not humanized.
 */
export function resolveExtraDetails(review: Review): [string, unknown][] {
  const item = review.reviewedItem as Record<string, unknown> | undefined;
  if (!item) return [];
  return Object.entries(item).filter(([key, value]) => !KNOWN_KEYS.has(key) && value !== undefined && value !== null);
}

/** Renders any JSON value for the details list: primitives as-is, arrays joined, objects as JSON. */
export function formatDetailValue(value: unknown): string {
  if (Array.isArray(value)) return value.map(formatDetailValue).join(', ');
  if (value && typeof value === 'object') return JSON.stringify(value);
  return String(value);
}
